// --- START OF FILE CaseStudy.jsx ---

import React from 'react';
import { motion } from 'framer-motion';
import { Server, DollarSign, Briefcase, CheckCircle } from 'lucide-react';

const CaseStudy = () => {
  return (
    <section id="case-study" className="py-20 relative bg-black/20 overflow-hidden">
      {/* Background accents using Peacock Blue -> Light Gold -> Primary Gold */}
      <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--peacock-blue))]/10 via-[hsl(var(--accent-cyan))]/10 to-transparent"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Featured Project: DeepGrid DataCenter</span> {/* Now uses Oxanium font */}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Architecting the reference data center for a revolutionary RISC-V Domain Specific SoC (DSoC).
          </p>
        </motion.div>

        <div className="glass-effect rounded-xl p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">The Challenge</h3>
                <p className="text-gray-300 leading-relaxed mb-8">
                  DeepGrid had developed a groundbreaking DSoC integrating compute, DNA-based storage, and ultra-low power consumption. They needed a strategic partner to design a scalable reference data center architecture to validate the chip's TCO (Total Cost of Ownership) and performance benefits against traditional hardware.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">The VegaVelox Solution</h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start">
                    {/* Using accent-cyan for consistency with new accent palette */}
                    <Server className="w-5 h-5 text-[hsl(var(--accent-cyan))] mr-3 mt-1 flex-shrink-0" />
                    <span>Designed a high-density, rack-scale architecture to maximize the DSoC's footprint and energy efficiency advantages.</span>
                  </li>
                  <li className="flex items-start">
                    <DollarSign className="w-5 h-5 text-[hsl(var(--accent-cyan))] mr-3 mt-1 flex-shrink-0" />
                    <span>Conducted a comprehensive Cost Savings Audit, modeling the projected power, cooling, and operational savings compared to legacy Intel/ARM-based systems.</span>
                  </li>
                  <li className="flex items-start">
                    <Briefcase className="w-5 h-5 text-[hsl(var(--accent-cyan))] mr-3 mt-1 flex-shrink-0" />
                    <span>Provided end-to-end Project Management for the PoC (Proof of Concept) build-out, coordinating with hardware vendors and software teams.</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="bg-black/20 rounded-lg p-8 border border-[hsl(var(--border))]">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">Impact &amp; Results</h3>
                <ul className="space-y-6">
                    <li className="flex items-center text-lg">
                        <CheckCircle className="w-7 h-7 text-[hsl(var(--peacock-blue))] mr-4 flex-shrink-0"/> {/* Use peacock-blue for success icon */}
                        <div>
                            <span className="font-semibold text-white">Validated Architecture</span>
                            <p className="text-gray-400 text-sm">Delivered a market-ready reference design for deploying the DeepGrid DSoC at scale.</p>
                        </div>
                    </li>
                    <li className="flex items-center text-lg">
                        <CheckCircle className="w-7 h-7 text-[hsl(var(--peacock-blue))] mr-4 flex-shrink-0"/> {/* Use peacock-blue for success icon */}
                        <div>
                            <span className="font-semibold text-white">Clear TCO Advantage</span>
                            <p className="text-gray-400 text-sm">Provided a detailed cost model proving significant long-term savings on energy and infrastructure.</p>
                        </div>
                    </li>
                    <li className="flex items-center text-lg">
                        <CheckCircle className="w-7 h-7 text-[hsl(var(--peacock-blue))] mr-4 flex-shrink-0"/> {/* Use peacock-blue for success icon */}
                        <div>
                            <span className="font-semibold text-white">Accelerated Market Entry</span>
                            <p className="text-gray-400 text-sm">Enabled DeepGrid to present a complete, validated solution to investors and early adopters.</p>
                        </div>
                    </li>
                </ul>
            </motion.div>

          </div>
        </div>
      </div>
    </section>

  );
};

export default CaseStudy;
// --- END OF FILE CaseStudy.jsx ---